import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Breadcrumbs,
    Link as MuiLink,
    CircularProgress
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import GenericModal from '../../components/GenericModal';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';
import {
    getPickupSheet,
    markAsReceivedAtHub,
    printPickupSheet,
    PickupSheetDetail
} from '../../services/pickupSheetApi';
import { ConsignmentView } from '../Consignments';

const PickupSheetView = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const user = useSelector((state: RootState) => state.auth.user);
    const { toast, showSuccess, showError, hideToast } = useToast();
    const [pickupSheet, setPickupSheet] = useState<PickupSheetDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedConsignmentId, setSelectedConsignmentId] = useState<number | null>(null);
    const [receivedConfirmOpen, setReceivedConfirmOpen] = useState(false);
    const [submittingReceived, setSubmittingReceived] = useState(false);

    const isClient = user?.type === 'CLIENT';
    const isAdmin = user?.type === 'ADMIN';

    useEffect(() => {
        fetchPickupSheet();
    }, [id]);

    const fetchPickupSheet = async () => {
        try {
            setLoading(true);
            const response = await getPickupSheet(Number(id));
            setPickupSheet(response.data);
        } catch (error: any) {
            showError(error.message || 'Failed to load pickup sheet');
            navigate('/pickup-sheets');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsReceivedAtHub = async () => {
        setSubmittingReceived(true);
        try {
            await markAsReceivedAtHub(Number(id));
            setReceivedConfirmOpen(false);
            showSuccess('Pickup sheet marked as received at hub!');
            fetchPickupSheet();
        } catch (error: any) {
            showError(error.message || 'Failed to mark as received at hub');
        } finally {
            setSubmittingReceived(false);
        }
    };

    const handleViewConsignment = (consignmentId: number) => {
        setSelectedConsignmentId(consignmentId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedConsignmentId(null);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusColor = (statusId?: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        if (!statusId) return 'default';
        if (statusId === 4) return 'success'; // Hand Over
        if (statusId === 3) return 'info'; // Picked Up
        if (statusId === 25) return 'error'; // Short Received
        return 'default';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!pickupSheet) {
        return null;
    }

    const canMarkAsReceived = !isClient && pickupSheet.status?.id === 3;
    const canPrint = pickupSheet.status?.id !== 19;

    // Calculate handover count
    const handoverCount = pickupSheet.consignments?.filter(c => c.pickupsheet_status?.id === 4).length || 0;

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/pickup-sheets')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Pickup Sheets
                </MuiLink>
                <Typography color="text.primary">{pickupSheet.unique_id}</Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Pickup Sheet
                        </Typography>
                        <Box>
                            {canPrint && (
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={() => printPickupSheet(Number(id))}
                                    sx={{ mr: 2 }}
                                >
                                    Print
                                </Button>
                            )}
                            {canMarkAsReceived && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => setReceivedConfirmOpen(true)}
                                >
                                    Mark as Received at Hub
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Basic Information */}
                    <Box sx={{ display: 'flex', gap: 3, mb: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                        <Box sx={{ flex: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                                    Basic Information
                                </Typography>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, border: 'none' }}>Total Consignments:</TableCell>
                                            <TableCell sx={{ border: 'none' }}>{pickupSheet.consignments?.length || 0}</TableCell>
                                        </TableRow>
                                        {!isClient && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, border: 'none' }}>Handover Count:</TableCell>
                                                <TableCell sx={{ border: 'none' }}>{handoverCount}</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, border: 'none' }}>Total COD Amount:</TableCell>
                                            <TableCell sx={{ border: 'none' }}>{pickupSheet.total_cod_amount || 0}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, border: 'none' }}>Rider:</TableCell>
                                            <TableCell sx={{ border: 'none' }}>{pickupSheet.rider?.name || '-'}</TableCell>
                                        </TableRow>
                                        {!isClient && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, border: 'none' }}>Client Name:</TableCell>
                                                <TableCell sx={{ border: 'none' }}>{pickupSheet.user?.name || '-'}</TableCell>
                                            </TableRow>
                                        )}
                                        {isAdmin && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, border: 'none' }}>Branch:</TableCell>
                                                <TableCell sx={{ border: 'none' }}>{pickupSheet.user?.branch?.name || '-'}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                                    Dates & Status
                                </Typography>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, border: 'none' }}>Created Date:</TableCell>
                                            <TableCell sx={{ border: 'none' }}>{formatDate(pickupSheet.created_at)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, border: 'none' }}>Pickup Date:</TableCell>
                                            <TableCell sx={{ border: 'none' }}>
                                                {pickupSheet.status?.id !== 2 && pickupSheet.pickedup_at
                                                    ? formatDate(pickupSheet.pickedup_at)
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, border: 'none' }}>Status:</TableCell>
                                            <TableCell sx={{ border: 'none' }}>
                                                <Chip
                                                    label={pickupSheet.status?.name || 'Unknown'}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    </Box>

                    {/* Consignments Table */}
                    <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                        Consignments
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Consignment #</TableCell>
                                    {isAdmin && <TableCell>Origin</TableCell>}
                                    {isClient && <TableCell>Order #</TableCell>}
                                    <TableCell>Destination</TableCell>
                                    <TableCell>Service Type</TableCell>
                                    <TableCell>Booking Date</TableCell>
                                    <TableCell>COD Amount</TableCell>
                                    <TableCell>Reason of Short Received</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pickupSheet.consignments && pickupSheet.consignments.length > 0 ? (
                                    pickupSheet.consignments.map((consignment) => (
                                        <TableRow key={consignment.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => handleViewConsignment(consignment.id)}
                                                    sx={{ textDecoration: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                                >
                                                    {consignment.consignment_no || consignment.tracking_number}
                                                </MuiLink>
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell>
                                                    {consignment.user?.Branch?.city?.name || '-'}
                                                    {consignment.user?.Branch?.name && ` (${consignment.user.Branch.name})`}
                                                </TableCell>
                                            )}
                                            {isClient && (
                                                <TableCell>{consignment.orderId || '-'}</TableCell>
                                            )}
                                            <TableCell>
                                                {consignment.destinationCity?.name || consignment.destination_city || '-'}
                                            </TableCell>
                                            <TableCell>{consignment.service_type || '-'}</TableCell>
                                            <TableCell>{formatDate(consignment.created_at)}</TableCell>
                                            <TableCell>{consignment.cod_amount || 0}</TableCell>
                                            <TableCell>
                                                {consignment.pickupsheet_status?.id === 25 ? (
                                                    <>
                                                        {consignment.reason}
                                                        {consignment.attachment && (
                                                            <Box sx={{ mt: 1 }}>
                                                                <MuiLink
                                                                    href={`/pickupsheet_attachments/${consignment.attachment}`}
                                                                    download
                                                                    sx={{ fontSize: '0.875rem' }}
                                                                >
                                                                    Download Attachment
                                                                </MuiLink>
                                                            </Box>
                                                        )}
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={consignment.pickupsheet_status?.name || 'Unknown'}
                                                    color={getStatusColor(consignment.pickupsheet_status?.id)}
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">No consignments found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Delay in Pickup Section */}
                    {!isClient && pickupSheet.pendings && pickupSheet.pendings.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                                Delay in Pickup
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Date/Time</TableCell>
                                            <TableCell>Reason</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pickupSheet.pendings.map((pending) => (
                                            <TableRow key={pending.id}>
                                                <TableCell>{pending.user?.name || '-'}</TableCell>
                                                <TableCell>{formatDateTime(pending.created_at)}</TableCell>
                                                <TableCell>{pending.description}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Consignment Details Modal */}
            <ConsignmentView
                open={modalOpen}
                onClose={handleCloseModal}
                consignmentId={selectedConsignmentId}
            />

            {/* Received at Hub Confirmation Modal */}
            <GenericModal
                open={receivedConfirmOpen}
                onClose={() => setReceivedConfirmOpen(false)}
                title="Mark as Received at Hub"
                maxWidth="xs"
                actions={
                    <>
                        <Button
                            onClick={() => setReceivedConfirmOpen(false)}
                            disabled={submittingReceived}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleMarkAsReceivedAtHub}
                            disabled={submittingReceived}
                            sx={{ borderRadius: 3 }}
                        >
                            {submittingReceived ? 'Processing...' : 'Yes, Mark as Received'}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    Are you sure you want to mark this pickup sheet as received at hub?
                </Typography>
            </GenericModal>

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default PickupSheetView;
